import {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Typography,
} from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';

import { AccountBase } from '@/models/AccountViewModel';
import {
  DiversificationScheme,
  useGetDiversificationCategoriesQuery,
} from '@/store/diversificationsApi';
import { OfflineAccount, useGetOfflineAccountQuery } from '@/store/accountsApi';
import { buildOfflineAccountDiversifications } from '@/modules/portfolio/accountModal/useAccountSave';
import ManualPortfolioDiversificationForm from '@/modules/portfolio/accountModal/ManualPortfolioDiversification/ManualPortfolioDiversificationForm';
import usePortfolioDiversificationCategories from '@/modules/portfolio/accountModal/ManualPortfolioDiversification/usePortfolioDiversificationCategories';
import { categoryUsesTreeRelationships } from '@/modules/portfolio/accountModal/ManualPortfolioDiversification/diversificationTree.utility';
import { sortAndDeduplicateByName } from '@/modules/portfolio/accountModal/ManualPortfolioDiversification/diversificationOptions.utility';
import { PortfolioDiversificationFormState } from '@/modules/portfolio/accountModal/ManualPortfolioDiversification/usePortfolioDiversificationForm';
import { RealEstateDetailsFormState } from '@/modules/portfolio/accountModal/ManualRealEstateDetails/useRealEstateDetailsForm';

interface PersistedDiversification {
  DiversificationId: number;
  Percent: number;
  ChildDiversifications?: PersistedDiversification[];
}

export interface AssetAllocationTabHandle {
  /** Returns the diversifications payload for the combined offline save. */
  getPayload: () => {
    Diversifications: ReturnType<typeof buildOfflineAccountDiversifications>;
  };
  /** Whether the allocation has unsaved changes. Used to prompt before closing. */
  hasChanges: () => boolean;
}

interface AssetAllocationTabProps {
  account: AccountBase;
  onValidationChange?: (valid: boolean) => void;
  onLoadingChange?: (loading: boolean) => void;
}

const EMPTY_FORM_STATE: PortfolioDiversificationFormState = {
  values: { Category: null, SubCategory: null, Detail: null },
  showDiversifications: false,
  isValid: true,
};

const signatureOf = (state: PortfolioDiversificationFormState): string =>
  JSON.stringify({
    category: state.values.Category?.DiversificationId ?? null,
    subCategory: state.values.SubCategory?.DiversificationId ?? null,
    detail: state.values.Detail?.DiversificationId ?? null,
    showDiversifications: state.showDiversifications,
  });

const AssetAllocationTab = forwardRef<
  AssetAllocationTabHandle,
  AssetAllocationTabProps
>(({ account, onValidationChange, onLoadingChange }, ref) => {
  const isOfflineAccount = account.AccountType === 2;

  const {
    allDiversifications,
    allDiversificationsLoading,
    allDiversificationsError,
  } = usePortfolioDiversificationCategories();

  const {
    data: diversificationCategoriesData,
    isLoading: diversificationCategoriesLoading,
  } = useGetDiversificationCategoriesQuery();

  const { data: offlineAccountData, isFetching: offlineFetching } =
    useGetOfflineAccountQuery(isOfflineAccount ? account.AccountId : skipToken);

  const [form, setForm] =
    useState<PortfolioDiversificationFormState>(EMPTY_FORM_STATE);

  const seededRef = useRef(false);
  const initialSignatureRef = useRef<string | null>(null);

  const realEstateDetailsForm = useMemo<RealEstateDetailsFormState>(
    () => ({
      values: {
        Name: '',
        Notes: '',
        AccountSubType: '',
        Address: '',
        PropertyValue: '',
        Zpid: '',
        ClassificationId: undefined,
        ParentId: undefined,
        SelectedTagIds: [],
      },
      isValid: false,
    }),
    [],
  );

  const schemeMap = useMemo(() => {
    const map = new Map<number, DiversificationScheme>();
    (allDiversifications?.Records ?? []).forEach((scheme) => {
      map.set(scheme.DiversificationId, scheme);
    });
    return map;
  }, [allDiversifications]);

  useEffect(() => {
    if (seededRef.current || !isOfflineAccount) return;
    if (offlineFetching || allDiversificationsLoading) return;
    if (diversificationCategoriesLoading) return;
    if (!offlineAccountData || schemeMap.size === 0) return;

    const persisted =
      (
        offlineAccountData as OfflineAccount & {
          Diversifications?: PersistedDiversification[];
        }
      ).Diversifications ?? [];

    let Category: DiversificationScheme | null = null;
    let SubCategory: DiversificationScheme | null = null;
    let Detail: DiversificationScheme | null = null;

    const options = sortAndDeduplicateByName(
      allDiversifications?.Records ?? [],
    );
    const categories = diversificationCategoriesData?.Records ?? [];

    const root = persisted.length > 0 ? persisted[0] : null;
    if (root) {
      Category = schemeMap.get(root.DiversificationId) ?? null;
      const child = root.ChildDiversifications?.[0];
      if (child) {
        const categoryIsTree = categoryUsesTreeRelationships({
          category: Category,
          options,
          categories,
        });
        if (categoryIsTree) {
          SubCategory = schemeMap.get(child.DiversificationId) ?? null;
          const grandchild = child.ChildDiversifications?.[0];
          Detail = grandchild
            ? (schemeMap.get(grandchild.DiversificationId) ?? null)
            : null;
        } else {
          Detail = schemeMap.get(child.DiversificationId) ?? null;
        }
      }
    }

    const seeded: PortfolioDiversificationFormState = {
      values: { Category, SubCategory, Detail },
      showDiversifications: persisted.length > 0,
      isValid: true,
    };

    setForm(seeded);
    initialSignatureRef.current = signatureOf(seeded);
    seededRef.current = true;
  }, [
    isOfflineAccount,
    offlineFetching,
    allDiversificationsLoading,
    diversificationCategoriesLoading,
    diversificationCategoriesData,
    offlineAccountData,
    schemeMap,
    allDiversifications,
  ]);

  const isLoading =
    allDiversificationsLoading ||
    diversificationCategoriesLoading ||
    (isOfflineAccount && offlineFetching);

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  const hasChanges = useCallback((): boolean => {
    if (initialSignatureRef.current === null) return false;
    return signatureOf(form) !== initialSignatureRef.current;
  }, [form]);

  useEffect(() => {
    const untouchedAllocationIsNotSaved = !hasChanges();
    onValidationChange?.(
      untouchedAllocationIsNotSaved ||
        !form.showDiversifications ||
        form.isValid,
    );
  }, [hasChanges, form.showDiversifications, form.isValid, onValidationChange]);

  const getPayload = useCallback(
    (): {
      Diversifications: ReturnType<typeof buildOfflineAccountDiversifications>;
    } => ({
      Diversifications: form.showDiversifications
        ? buildOfflineAccountDiversifications(form.values)
        : [],
    }),
    [form],
  );

  useImperativeHandle(
    ref,
    () => ({
      getPayload,
      hasChanges,
    }),
    [getPayload, hasChanges],
  );

  if (!isOfflineAccount) return null;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (allDiversificationsError) {
    return (
      <Typography color='error'>
        Unable to load diversification schemes. Please try again later.
      </Typography>
    );
  }

  return (
    <Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={form.showDiversifications}
            onChange={(event) => {
              const checked = event.target.checked;
              setForm((prev) => ({
                ...prev,
                showDiversifications: checked,
                isValid: checked ? prev.isValid : true,
              }));
            }}
          />
        }
        label="Set Account's Asset Allocation"
      />
      {form.showDiversifications && (
        <ManualPortfolioDiversificationForm
          portfolioDiversificationForm={form}
          onFormChange={setForm}
          selectedClassification={null}
          realEstateDetailsForm={realEstateDetailsForm}
        />
      )}
    </Box>
  );
});

export default AssetAllocationTab;
